import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import _ from 'underscore';

import { SortableHeader } from '~src/spacesPlatform/components/ListView/SortableHeader';
import { StyledCellFormatter } from '~src/spacesPlatform/components/ListView/StyledCellWrapper';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { Messages } from '../../../codegen/Localize';
import { IDataConnectionInfo } from '../../../ts/models/IDataConnectionInfo';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { FlowOverviewErrorsCellComponent } from '../../FlowDataGridDefinitions';
import { FlowDetailWithUsageInfo } from '../../models/FlowInfo';
import { FlowOutputStepRunListItemInfo } from '../../models/FlowOutputStepRunInfo';
import { FlowRunErrorUniqueKey } from '../../serverApi/types/FlowRunError';
import { FlowConnectionsSubplaceMetadata } from '../FlowConnectionsSubplace';

export function FlowConnectionErrorColumn(params: {
  readonly spaceInfo: SpaceInfo;
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly siteUrls: SiteUrls;
}): DataGrid.Column {
  const sortableHeaderFormatter = SortableHeader.Formatter(params.currentSort, params.changeSort);
  const flow = params.spaceInfo.item as FlowDetailWithUsageInfo & FlowConnectionsSubplaceMetadata;
  const mostRecentRuns = flow.subplaceMetadata?.mostRecentRuns ?? [];
  const isDownloadAllowed = flow.subplaceMetadata?.flowAllowedActions.download ?? false;

  return {
    name: 'errors-col',
    dataKey: 'errors',
    headerText: Messages.flowRunHistoryColumn_errors(),
    sortable: false,
    headerFormatter: sortableHeaderFormatter,
    cellFormatter: StyledCellFormatter(
      ConnectionErrorCell(flow.id, mostRecentRuns, params.siteUrls, isDownloadAllowed)
    ),
  };
}

function ConnectionErrorCell(
  flowId: string,
  mostRecentRuns: ReadonlyArray<FlowOutputStepRunListItemInfo>,
  siteUrls: SiteUrls,
  isDownloadAllowed: boolean,
): React.FunctionComponent<BodyCell.Props> {
  return function ConnectionErrorCell(props: BodyCell.Props) {
    const connection = props.row.data as IDataConnectionInfo;

    const uniqueConnectionErrors = mostRecentRuns == null ? [] : _
      .chain(mostRecentRuns.map(run => run.flowRunErrors))
      .flatten()
      .filter(error => error.errorObjectId === connection.id)
      .uniq(false, FlowRunErrorUniqueKey)
      .value();

    // Note (@abrandon 2020-12-29): the `isDownloadAllowed` param is currently always assigned `true`
    // It was originally implemented by @jjia, who confirmed that it's supposed to be connected to
    // the prop of the same name. However, since no defects have been reported, I left it as-is when
    // I migrated this file.
    return (
      <FlowOverviewErrorsCellComponent
        errors={uniqueConnectionErrors}
        flowId={flowId}
        onConnectionsPage={true}
        siteUrls={siteUrls}
        isDownloadAllowed
      />
    );
  };
}
